import type {
  CarAccomodation,
  ContentTypeErpStoryblok,
} from '@/types/storyblok'

import type { ContentTypeErpStoryblok as BaseContentTypeErpStoryblok } from '@avjennings/storyblok/types/storyblok'
import type { PropertyFilterUrlParams } from '@/components/property/PropertyFilters.vue'

// Get a filtered list of ContentTypeERP stories and return an array of their UUIDs
export default async function (params: {
  [k in PropertyFilterUrlParams]?: MaybeRefOrGetter<string>
}) {
  /**
   * Excluding all values in the story.content to get a smaller (and faster) response
   */
  const excludeFields: Array<keyof BaseContentTypeErpStoryblok> = [
    'Aspect',
    'Available',
    'BathRooms',
    'Bedrooms',
    'BikeSpace',
    'BuildingID',
    'BuildingType',
    'BuiltFormType',
    'CPAllocation',
    'CPNumber',
    'CarAccomodation',
    'ColorCode',
    'ConCompActualdate',
    'ConCompForecastdate',
    'ConIssueActualdate',
    'ConIssueForecastdate',
    'ConSignActualdate',
    'ConSignForecastdate',
    'Corner',
    'CustomerID',
    'DEPTH',
    'DepositActualdate',
    'DepositForecastdate',
    'Devstatus',
    'Estate',
    'ExternalArea',
    'FacadeType',
    'FinApvActualdate',
    'FinApvForecastdate',
    'GasElectric',
    'InternalFloorArea',
    'Irregular',
    'JobAddress',
    'JobName',
    'JobStatus',
    'LandArea',
    'Level',
    'ListPrice',
    'NumOfLots',
    'Orientation',
    'PK_ProjID',
    'PK_dataareaId',
    'Project',
    'ProjectType',
    'RegistrationNo',
    'STAGE',
    'Series',
    'SettleActualdate',
    'SettleForecastdate',
    'State',
    'StockStatus',
    'StorageAllocation',
    'Storeys',
    'Study',
    'SubProjectType',
    'TotalArea',
    'UnConActualdate',
    'UnConForecastdate',
    'WIDTH',
    'component',
    '_editable',
    '_uid',
  ]

  type StatusQuery =
    | {
        JobStatus: { in: string }
      }
    | {
        Devstatus: { in: string }
        JobStatus: { is: 'empty' }
      }
    | undefined
  /**
   * Converts a Content Property value to the corresponding filter_query for an SB stories fetch.
   * @param status Content Property value
   * @returns StatusQuery
   */
  const convertStatusToQuery = (status: string): StatusQuery => {
    const { resolveContentPropertyStatus } = usePropertyStatus()
    const { JobStatus, Devstatus } = resolveContentPropertyStatus(status)

    if (JobStatus) {
      return {
        JobStatus: { in: JobStatus },
      }
    }

    if (Devstatus) {
      return {
        Devstatus: { in: Devstatus },
        JobStatus: { is: 'empty' },
      }
    }

    return undefined
  }

  /**
   * Computed value for the entire filter_query to be passed in as an API option
   */
  const erpFilterQuery = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const query: Record<keyof BaseContentTypeErpStoryblok, any> = {}

    const propertyType = toValue(params.propertyType)
    const bathrooms = toValue(params.bathrooms)
    const bedrooms = toValue(params.bedrooms)
    const parking = toValue(params.parking)
    const priceRangeMin = toValue(params['price-range-min'])
    const priceRangeMax = toValue(params['price-range-max'])
    const landSizeMin = toValue(params['land-size-min'])
    const landSizeMax = toValue(params['land-size-max'])
    const status = toValue(params.status)

    const resolvedPropertyType = resolveContentPropertyType(
      propertyType as PropertyType,
    )
    if (propertyType) {
      query.ProjectType = {
        in: resolvedPropertyType,
      }
    }

    if (bathrooms) {
      query.BathRooms = {
        gt_float: parseFloat(bathrooms) - 0.5,
      }
    }

    if (bedrooms) {
      query.Bedrooms = {
        gt_float: parseFloat(bedrooms) - 0.5,
      }
    }

    if (parking) {
      // If not filtering by property type, look at either field for parking
      const parkingQueries = {}

      if (!propertyType || propertyType === 'apartment') {
        Object.assign(parkingQueries, {
          CPAllocation: {
            gt_int: parseInt(parking) - 1,
          },
        })
      }

      if (!propertyType || propertyType === 'home') {
        const spaces = parseInt(parking)

        let carAccoms: CarAccomodation[] = []
        for (let i = spaces; i <= 3; i++) {
          carAccoms = carAccoms.concat(carSpacesToCarAccomodation(i) || [])
        }

        Object.assign(parkingQueries, {
          CarAccomodation: {
            in: carAccoms.join(','),
          },
        })
      }

      if (!propertyType) {
        query.__or = [parkingQueries]
      } else {
        Object.assign(query, parkingQueries)
      }
    }

    if (priceRangeMin || priceRangeMax) {
      query.ListPrice = {}
      if (priceRangeMin) {
        query.ListPrice.gt_float = parseFloat(priceRangeMin) - 0.01
      }
      if (priceRangeMax) {
        query.ListPrice.lt_float = parseFloat(priceRangeMax) + 0.01
      }
    }

    if (landSizeMin || landSizeMax) {
      query.LandArea = {}
      if (landSizeMin) {
        query.LandArea.gt_float = parseFloat(landSizeMin) - 0.01
      }
      if (landSizeMax) {
        query.LandArea.lt_float = parseFloat(landSizeMax) + 0.01
      }
    }

    if (status) {
      Object.assign(query, convertStatusToQuery(status))
    } else {
      // Hide sold properties if not explicitly selecting it as a filter
      const { JobStatus } =
        usePropertyStatus().resolveContentPropertyStatus('sold')
      query.JobStatus = { not_in: JobStatus }
    }

    return query
  })

  const apiOptions = computed<FetchAPIOptions>(() => ({
    content_type: 'ContentTypeERP',
    page: 1,
    per_page: 100,
    excluding_fields: excludeFields.join(','),
    filter_query: erpFilterQuery.value,
  }))

  const { fetchAsyncDataPaginated } = useStoryblokHelpers()
  const { data, clear, refresh } =
    await fetchAsyncDataPaginated<ContentTypeErpStoryblok>(
      'stories',
      apiOptions,
      {
        watch: [apiOptions],
        getCachedData(key, nuxtApp) {
          return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
        },
      },
    )

  const filteredUuids = computed(() =>
    data.value?.data.stories.map((story) => story.uuid),
  )

  return {
    data,
    clear,
    refresh,
    filteredUuids,
  }
}
